import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { db, storage } from '../../firebase';
import { doc, setDoc, collection, serverTimestamp } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { useAuth } from '../../context/AuthContext';
import PhotoCropModal from '../../components/ui/PhotoCropModal';
import {
  IconShieldCheck,
  IconCheck,
  IconTruck,
  IconUsers,
  IconMail,
  IconDroplet,
  IconPlus,
  IconTrash,
  IconX,
  IconArrowRight,
  IconLock,
  IconCamera,
} from '@tabler/icons-react';

const STEPS = [
  { num: 1, label: 'Confirm details' },
  { num: 2, label: 'Add assets' },
  { num: 3, label: 'Invite staff' },
  { num: 4, label: 'Derv tank' },
  { num: 5, label: 'Connect Outlook' },
];

const ASSET_TYPES = ['Vehicle', 'Trailer', 'Machinery'];

export default function ManagerOnboarding() {
  const { userProfile, orgId, user } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(1);

  // Step 1
  const [firstName, setFirstName] = useState(userProfile?.name?.split(' ')[0] || '');
  const [lastName, setLastName] = useState(userProfile?.name?.split(' ').slice(1).join(' ') || '');
  const [jobTitle, setJobTitle] = useState('');
  const [phone, setPhone] = useState('');
  const [photoURL, setPhotoURL] = useState(userProfile?.photoURL || null);
  const [showCropModal, setShowCropModal] = useState(false);

  // Step 2
  const [assets, setAssets] = useState([]);
  const [showAssetModal, setShowAssetModal] = useState(false);
  const [assetReg, setAssetReg] = useState('');
  const [assetMake, setAssetMake] = useState('');
  const [assetModel, setAssetModel] = useState('');
  const [assetType, setAssetType] = useState('Vehicle');
  const [assetPool, setAssetPool] = useState(false);
  const [dvlaLoading, setDvlaLoading] = useState(false);

  // Step 3
  const [staff, setStaff] = useState([]);
  const [showStaffModal, setShowStaffModal] = useState(false);
  const [staffEmail, setStaffEmail] = useState('');
  const [staffRole, setStaffRole] = useState('operator');

  // Step 4
  const [hasTank, setHasTank] = useState(null);
  const [tankName, setTankName] = useState('Main Derv');
  const [tankCapacity, setTankCapacity] = useState('');
  const [tankCurrent, setTankCurrent] = useState('');
  const [tankLowThreshold, setTankLowThreshold] = useState(30);
  const [tankOrderThreshold, setTankOrderThreshold] = useState(15);

  const handleNext = () => setStep(s => s + 1);
  const handleBack = () => setStep(s => s - 1);

  const handlePhotoSave = async (blob) => {
    try {
      const storageRef = ref(storage, `organisations/${orgId}/avatars/${user.uid}`);
      await uploadBytes(storageRef, blob);
      const url = await getDownloadURL(storageRef);
      setPhotoURL(url);
    } catch (err) {
      console.error('Photo upload error:', err);
      throw err;
    }
  };

  const handleDvlaLookup = async () => {
    if (!assetReg) return;
    setDvlaLoading(true);
    await new Promise(r => setTimeout(r, 800));
    setAssetMake('Ford');
    setAssetModel('Transit');
    setAssetType('Vehicle');
    setDvlaLoading(false);
  };

  const handleAddAsset = () => {
    if (!assetReg && assetType === 'Vehicle') return;
    setAssets(prev => [...prev, { reg: assetReg, make: assetMake, model: assetModel, type: assetType.toLowerCase(), isPool: assetPool }]);
    setAssetReg(''); setAssetMake(''); setAssetModel(''); setAssetType('Vehicle'); setAssetPool(false);
    setShowAssetModal(false);
  };

  const handleAddStaff = () => {
    if (!staffEmail) return;
    setStaff(prev => [...prev, { email: staffEmail, role: staffRole }]);
    setStaffEmail(''); setStaffRole('operator');
    setShowStaffModal(false);
  };

  const handleFinish = async () => {
    try {
      const branchId = userProfile?.branchId;
      for (const asset of assets) {
        const assetRef = doc(collection(db, 'organisations', orgId, 'branches', branchId, 'assets'));
        await setDoc(assetRef, { ...asset, isBlocked: false, createdAt: serverTimestamp() });
      }
      if (hasTank === 'yes') {
        const tankRef = doc(collection(db, 'organisations', orgId, 'branches', branchId, 'dervTanks'));
        await setDoc(tankRef, { name: tankName, capacityLitres: Number(tankCapacity), currentLitres: Number(tankCurrent), lowThresholdPct: tankLowThreshold, orderThresholdPct: tankOrderThreshold, createdAt: serverTimestamp() });
      }
      await setDoc(doc(db, 'organisations', orgId, 'users', user.uid), {
        name: `${firstName} ${lastName}`.trim(),
        jobTitle, phone,
        photoURL: photoURL || null,
        onboardingComplete: true,
        updatedAt: serverTimestamp(),
      }, { merge: true });
      navigate('/dashboard');
    } catch (err) {
      console.error(err);
    }
  };

  const roleColours = {
    operator: { bg: '#EAF3DE', color: '#3B6D11' },
    driver: { bg: '#EEEDFE', color: '#534AB7' },
  };

  const initials = `${firstName[0] || ''}${lastName[0] || ''}`.toUpperCase() || '?';

  return (
    <div style={{ display: 'flex', height: '100vh', width: '100vw', fontFamily: 'inherit', overflow: 'hidden' }}>

      {/* Left panel */}
      <div style={{ width: '28%', background: 'var(--color-brand)', padding: '40px 32px', display: 'flex', flexDirection: 'column', flexShrink: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 40 }}>
          <div style={{ width: 36, height: 36, borderRadius: 'var(--radius-md)', background: 'rgba(255,255,255,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <IconShieldCheck size={18} color="white" stroke={1.5} />
          </div>
          <div style={{ fontSize: 16, fontWeight: 400, color: '#fff' }}>Veritas</div>
        </div>
        <div style={{ flex: 1 }}>
          {STEPS.map((s, i) => {
            const isDone = step > s.num;
            const isActive = step === s.num;
            return (
              <div key={s.num}>
                <div style={{ display: 'flex', gap: 12, paddingBottom: 20 }}>
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: 24, flexShrink: 0 }}>
                    <div style={{ width: 24, height: 24, borderRadius: '50%', background: isDone ? '#1D9E75' : isActive ? '#fff' : 'rgba(255,255,255,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, color: isActive ? 'var(--color-brand)' : 'rgba(255,255,255,0.5)', flexShrink: 0 }}>
                      {isDone ? <IconCheck size={13} stroke={2} color="white" /> : s.num}
                    </div>
                    {i < STEPS.length - 1 && <div style={{ width: 1, flex: 1, background: 'rgba(255,255,255,0.2)', marginTop: 4 }} />}
                  </div>
                  <div style={{ flex: 1, paddingTop: 3 }}>
                    <div style={{ fontSize: 13, color: isDone ? 'rgba(255,255,255,0.6)' : isActive ? '#fff' : 'rgba(255,255,255,0.4)' }}>{s.label}</div>
                    {isActive && userProfile?.branchId && (
                      <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.5)', marginTop: 2 }}>{userProfile.branchId} · {userProfile.departmentId}</div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
        <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.35)' }}>
          An <span style={{ color: 'rgba(255,255,255,0.45)' }}>AGRIWARE</span> product &copy; 2026
        </div>
      </div>

      {/* Main content */}
      <div style={{ flex: 1, background: 'var(--color-bg-tertiary)', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        <div style={{ flex: 1, overflow: 'auto', padding: '48px 64px' }}>
          <div style={{ fontSize: 11, color: 'var(--color-text-tertiary)', letterSpacing: '0.05em', marginBottom: 8 }}>STEP {step} OF {STEPS.length}</div>

          {/* ── Step 1 ── */}
          {step === 1 && (
            <>
              <h1 style={{ fontSize: 28, fontWeight: 400, marginBottom: 6 }}>Confirm your details</h1>
              <p style={{ fontSize: 14, color: 'var(--color-text-secondary)', marginBottom: 36, lineHeight: 1.6 }}>
                Check your details are correct before we get your assets and team set up.
              </p>

              <div style={{ display: 'flex', gap: 12, marginBottom: 28 }}>
                {[
                  { label: 'Branch', value: userProfile?.branchId || '—' },
                  { label: 'Department', value: userProfile?.departmentId || '—' },
                  { label: 'Role', value: 'Manager' },
                ].map(item => (
                  <div key={item.label} style={{ background: 'var(--color-bg-secondary)', border: '0.5px solid var(--color-border-light)', borderRadius: 'var(--radius-md)', padding: '8px 14px', display: 'flex', alignItems: 'center', gap: 6, fontSize: 12 }}>
                    <IconLock size={11} stroke={1.5} color="var(--color-text-tertiary)" />
                    <span style={{ color: 'var(--color-text-tertiary)' }}>{item.label}:</span>
                    <span style={{ fontWeight: 400, color: 'var(--color-text-primary)' }}>{item.value}</span>
                  </div>
                ))}
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: 48, alignItems: 'start', marginBottom: 28 }}>

                {/* Avatar */}
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10 }}>
                  <div
                    onClick={() => setShowCropModal(true)}
                    style={{ width: 100, height: 100, borderRadius: '50%', background: photoURL ? 'transparent' : '#FDF0E8', backgroundImage: photoURL ? `url(${photoURL})` : 'none', backgroundSize: 'cover', backgroundPosition: 'center', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 32, color: '#993C1D', cursor: 'pointer', position: 'relative', overflow: 'hidden', border: '2px solid transparent', transition: 'border-color 0.15s' }}
                    onMouseEnter={e => e.currentTarget.style.borderColor = 'var(--color-brand)'}
                    onMouseLeave={e => e.currentTarget.style.borderColor = 'transparent'}
                  >
                    {!photoURL && initials}
                    <div
                      style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0)', display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'background 0.2s', borderRadius: '50%' }}
                      onMouseEnter={e => { e.currentTarget.style.background = 'rgba(0,0,0,0.35)'; e.currentTarget.querySelector('svg') && (e.currentTarget.querySelector('svg').style.opacity = '1'); }}
                      onMouseLeave={e => { e.currentTarget.style.background = 'rgba(0,0,0,0)'; e.currentTarget.querySelector('svg') && (e.currentTarget.querySelector('svg').style.opacity = '0'); }}
                    >
                      <IconCamera size={22} color="white" stroke={1.5} style={{ opacity: 0, transition: 'opacity 0.2s' }} />
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <span onClick={() => setShowCropModal(true)} style={{ fontSize: 12, color: 'var(--color-brand)', cursor: 'pointer' }}>
                      {photoURL ? 'Change' : 'Add photo'}
                    </span>
                    {photoURL && (
                      <>
                        <span style={{ color: 'var(--color-text-tertiary)' }}>·</span>
                        <span onClick={() => setPhotoURL(null)} style={{ fontSize: 12, color: 'var(--color-text-tertiary)', cursor: 'pointer' }}>Remove</span>
                      </>
                    )}
                  </div>
                  <div style={{ fontSize: 10, color: 'var(--color-text-tertiary)', textAlign: 'center', maxWidth: 100 }}>JPG, PNG or WebP</div>
                </div>

                {/* Fields */}
                <div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
                    <div>
                      <label style={{ display: 'block', fontSize: 11, color: 'var(--color-text-secondary)', marginBottom: 6 }}>First name</label>
                      <input type="text" value={firstName} onChange={e => setFirstName(e.target.value)} style={{ width: '100%', boxSizing: 'border-box', background: '#fff' }} />
                    </div>
                    <div>
                      <label style={{ display: 'block', fontSize: 11, color: 'var(--color-text-secondary)', marginBottom: 6 }}>Last name</label>
                      <input type="text" value={lastName} onChange={e => setLastName(e.target.value)} style={{ width: '100%', boxSizing: 'border-box', background: '#fff' }} />
                    </div>
                  </div>
                  <div style={{ marginBottom: 16 }}>
                    <label style={{ display: 'block', fontSize: 11, color: 'var(--color-text-secondary)', marginBottom: 6 }}>Job title</label>
                    <input type="text" placeholder="e.g. Workshop Manager" value={jobTitle} onChange={e => setJobTitle(e.target.value)} style={{ width: '100%', boxSizing: 'border-box', background: '#fff' }} />
                  </div>
                  <div style={{ marginBottom: 16 }}>
                    <label style={{ display: 'block', fontSize: 11, color: 'var(--color-text-secondary)', marginBottom: 6 }}>Phone number</label>
                    <input type="tel" placeholder="+44 7700 900 000" value={phone} onChange={e => setPhone(e.target.value)} style={{ width: '100%', boxSizing: 'border-box', background: '#fff' }} />
                  </div>
                  <div>
                    <label style={{ display: 'block', fontSize: 11, color: 'var(--color-text-secondary)', marginBottom: 6 }}>Work email</label>
                    <input type="email" value={userProfile?.email || ''} readOnly style={{ width: '100%', boxSizing: 'border-box', background: 'var(--color-bg-secondary)', color: 'var(--color-text-tertiary)' }} />
                    <div style={{ fontSize: 10, color: 'var(--color-text-tertiary)', marginTop: 4 }}>Change at any time in profile settings</div>
                  </div>
                </div>
              </div>
            </>
          )}

          {/* ── Step 2 ── */}
          {step === 2 && (
            <>
              <h1 style={{ fontSize: 28, fontWeight: 400, marginBottom: 6 }}>Add your assets</h1>
              <p style={{ fontSize: 14, color: 'var(--color-text-secondary)', marginBottom: 28, lineHeight: 1.6 }}>
                Add your vehicles, trailers and machinery. Enter a registration number and we'll pull the details from the DVLA automatically.
              </p>
              {assets.length > 0 && (
                <div style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 11, background: 'var(--color-brand-light)', color: 'var(--color-brand)', borderRadius: 'var(--radius-full)', padding: '3px 10px', marginBottom: 16 }}>
                  <IconTruck size={12} stroke={1.5} />
                  {assets.length} {assets.length === 1 ? 'asset' : 'assets'} added
                </div>
              )}
              <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                {assets.map((a, i) => (
                  <div key={i} style={{ width: 150, height: 150, background: '#fff', border: '0.5px solid var(--color-border-light)', borderRadius: 'var(--radius-lg)', padding: '16px 14px', display: 'flex', flexDirection: 'column', position: 'relative' }}>
                    <div style={{ width: 36, height: 36, borderRadius: 'var(--radius-md)', background: '#E6F1FB', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 8 }}>
                      <IconTruck size={18} color="#185FA5" stroke={1.5} />
                    </div>
                    <div style={{ fontSize: 12, fontWeight: 400, color: 'var(--color-text-primary)', marginBottom: 3 }}>{a.make} {a.model}</div>
                    {a.reg && <div style={{ fontFamily: 'monospace', fontSize: 10, background: '#FAC775', color: '#412402', padding: '1px 5px', borderRadius: 3, display: 'inline-block', marginBottom: 4 }}>{a.reg}</div>}
                    <div style={{ fontSize: 10, color: 'var(--color-text-tertiary)' }}>{a.type} {a.isPool ? '· Pool' : ''}</div>
                    <div style={{ position: 'absolute', bottom: 10, left: 0, right: 0, display: 'flex', justifyContent: 'center' }}>
                      <span onClick={() => setAssets(prev => prev.filter((_, j) => j !== i))} style={{ fontSize: 11, color: 'var(--color-text-tertiary)', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 3 }}>
                        <IconTrash size={11} stroke={1.5} /> Remove
                      </span>
                    </div>
                  </div>
                ))}
                <div onClick={() => setShowAssetModal(true)} style={{ width: 150, height: 150, border: '1.5px dashed var(--color-border-medium)', borderRadius: 'var(--radius-lg)', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 8, cursor: 'pointer', background: 'transparent', transition: 'border-color 0.15s, background 0.15s' }}
                  onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--color-brand)'; e.currentTarget.style.background = 'var(--color-brand-light)'; }}
                  onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--color-border-medium)'; e.currentTarget.style.background = 'transparent'; }}
                >
                  <div style={{ width: 36, height: 36, borderRadius: '50%', background: 'var(--color-bg-secondary)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <IconPlus size={18} color="var(--color-text-tertiary)" stroke={1.5} />
                  </div>
                  <span style={{ fontSize: 12, color: 'var(--color-text-tertiary)' }}>Add asset</span>
                </div>
              </div>
            </>
          )}

          {/* ── Step 3 ── */}
          {step === 3 && (
            <>
              <h1 style={{ fontSize: 28, fontWeight: 400, marginBottom: 6 }}>Invite your team</h1>
              <p style={{ fontSize: 14, color: 'var(--color-text-secondary)', marginBottom: 16, lineHeight: 1.6 }}>
                Invite operators and drivers to your branch.
              </p>
              <div style={{ display: 'flex', gap: 12, marginBottom: 24 }}>
                {[{ label: 'Branch', value: userProfile?.branchId || '—' }, { label: 'Department', value: userProfile?.departmentId || '—' }].map(item => (
                  <div key={item.label} style={{ background: 'var(--color-bg-secondary)', border: '0.5px solid var(--color-border-light)', borderRadius: 'var(--radius-md)', padding: '8px 14px', display: 'flex', alignItems: 'center', gap: 6, fontSize: 12 }}>
                    <IconLock size={11} stroke={1.5} color="var(--color-text-tertiary)" />
                    <span style={{ color: 'var(--color-text-tertiary)' }}>{item.label}:</span>
                    <span style={{ fontWeight: 400, color: 'var(--color-text-primary)' }}>{item.value}</span>
                  </div>
                ))}
              </div>
              {staff.length > 0 && (
                <div style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 11, background: 'var(--color-brand-light)', color: 'var(--color-brand)', borderRadius: 'var(--radius-full)', padding: '3px 10px', marginBottom: 16 }}>
                  <IconUsers size={12} stroke={1.5} />
                  {staff.length} {staff.length === 1 ? 'invitation sent' : 'invitations sent'}
                </div>
              )}
              <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                {staff.map((s, i) => (
                  <div key={i} style={{ width: 150, height: 150, background: '#fff', border: '0.5px solid var(--color-border-light)', borderRadius: 'var(--radius-lg)', padding: '16px 14px', display: 'flex', flexDirection: 'column', alignItems: 'center', position: 'relative' }}>
                    <div style={{ width: 40, height: 40, borderRadius: '50%', background: roleColours[s.role]?.bg || '#EAF3DE', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, color: roleColours[s.role]?.color || '#3B6D11', marginBottom: 8 }}>
                      {s.email.slice(0, 2).toUpperCase()}
                    </div>
                    <div style={{ fontSize: 11, color: 'var(--color-text-primary)', textAlign: 'center', marginBottom: 6, wordBreak: 'break-all' }}>{s.email}</div>
                    <span style={{ fontSize: 9, padding: '1px 6px', borderRadius: 20, background: roleColours[s.role]?.bg, color: roleColours[s.role]?.color }}>
                      {s.role.charAt(0).toUpperCase() + s.role.slice(1)}
                    </span>
                    <div style={{ position: 'absolute', bottom: 10, left: 0, right: 0, display: 'flex', justifyContent: 'center' }}>
                      <span onClick={() => setStaff(prev => prev.filter((_, j) => j !== i))} style={{ fontSize: 11, color: 'var(--color-text-tertiary)', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 3 }}>
                        <IconTrash size={11} stroke={1.5} /> Remove
                      </span>
                    </div>
                  </div>
                ))}
                <div onClick={() => setShowStaffModal(true)} style={{ width: 150, height: 150, border: '1.5px dashed var(--color-border-medium)', borderRadius: 'var(--radius-lg)', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 8, cursor: 'pointer', background: 'transparent', transition: 'border-color 0.15s, background 0.15s' }}
                  onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--color-brand)'; e.currentTarget.style.background = 'var(--color-brand-light)'; }}
                  onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--color-border-medium)'; e.currentTarget.style.background = 'transparent'; }}
                >
                  <div style={{ width: 36, height: 36, borderRadius: '50%', background: 'var(--color-bg-secondary)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <IconPlus size={18} color="var(--color-text-tertiary)" stroke={1.5} />
                  </div>
                  <span style={{ fontSize: 12, color: 'var(--color-text-tertiary)' }}>Invite staff</span>
                </div>
              </div>
            </>
          )}

          {/* ── Step 4 ── */}
          {step === 4 && (
            <>
              <h1 style={{ fontSize: 28, fontWeight: 400, marginBottom: 6 }}>Derv tank</h1>
              <p style={{ fontSize: 14, color: 'var(--color-text-secondary)', marginBottom: 28, lineHeight: 1.6 }}>
                Does your branch have a Derv fuel tank?
              </p>
              {hasTank === null && (
                <div style={{ display: 'flex', gap: 12 }}>
                  {['yes', 'no'].map(opt => (
                    <div key={opt} onClick={() => setHasTank(opt)} style={{ flex: 1, maxWidth: 200, background: '#fff', border: '0.5px solid var(--color-border-light)', borderRadius: 'var(--radius-lg)', padding: '24px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10, cursor: 'pointer', transition: 'border-color 0.15s' }}
                      onMouseEnter={e => e.currentTarget.style.borderColor = 'var(--color-brand)'}
                      onMouseLeave={e => e.currentTarget.style.borderColor = 'var(--color-border-light)'}
                    >
                      <div style={{ width: 48, height: 48, borderRadius: '50%', background: opt === 'yes' ? 'var(--color-success-light)' : 'var(--color-bg-secondary)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        {opt === 'yes' ? <IconDroplet size={24} color="var(--color-success)" stroke={1.5} /> : <IconX size={24} color="var(--color-text-tertiary)" stroke={1.5} />}
                      </div>
                      <div style={{ fontSize: 14, fontWeight: 400 }}>{opt === 'yes' ? 'Yes, we have one' : 'No, skip this'}</div>
                    </div>
                  ))}
                </div>
              )}
              {hasTank === 'yes' && (
                <div style={{ maxWidth: 560 }}>
                  <div style={{ marginBottom: 16 }}>
                    <label style={{ display: 'block', fontSize: 11, color: 'var(--color-text-secondary)', marginBottom: 6 }}>Tank name</label>
                    <input type="text" value={tankName} onChange={e => setTankName(e.target.value)} style={{ width: '100%', boxSizing: 'border-box', background: '#fff' }} />
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
                    <div>
                      <label style={{ display: 'block', fontSize: 11, color: 'var(--color-text-secondary)', marginBottom: 6 }}>Capacity (litres)</label>
                      <input type="number" placeholder="5000" value={tankCapacity} onChange={e => setTankCapacity(e.target.value)} style={{ width: '100%', boxSizing: 'border-box', background: '#fff' }} />
                    </div>
                    <div>
                      <label style={{ display: 'block', fontSize: 11, color: 'var(--color-text-secondary)', marginBottom: 6 }}>Current level (litres)</label>
                      <input type="number" placeholder="3200" value={tankCurrent} onChange={e => setTankCurrent(e.target.value)} style={{ width: '100%', boxSizing: 'border-box', background: '#fff' }} />
                    </div>
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
                    <div>
                      <label style={{ display: 'block', fontSize: 11, color: 'var(--color-text-secondary)', marginBottom: 6 }}>Low alert threshold (%)</label>
                      <input type="number" value={tankLowThreshold} onChange={e => setTankLowThreshold(e.target.value)} style={{ width: '100%', boxSizing: 'border-box', background: '#fff' }} />
                    </div>
                    <div>
                      <label style={{ display: 'block', fontSize: 11, color: 'var(--color-text-secondary)', marginBottom: 6 }}>Auto-order threshold (%)</label>
                      <input type="number" value={tankOrderThreshold} onChange={e => setTankOrderThreshold(e.target.value)} style={{ width: '100%', boxSizing: 'border-box', background: '#fff' }} />
                    </div>
                  </div>
                  <button onClick={() => setHasTank(null)} style={{ fontSize: 12, color: 'var(--color-text-tertiary)', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>← Change answer</button>
                </div>
              )}
              {hasTank === 'no' && (
                <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, background: 'var(--color-bg-secondary)', borderRadius: 'var(--radius-md)', padding: '12px 16px', fontSize: 13, color: 'var(--color-text-secondary)' }}>
                  No Derv tank — you can add one later in settings.
                  <span onClick={() => setHasTank(null)} style={{ color: 'var(--color-brand)', cursor: 'pointer', fontSize: 12 }}>Change</span>
                </div>
              )}
            </>
          )}

          {/* ── Step 5 ── */}
          {step === 5 && (
            <>
              <h1 style={{ fontSize: 28, fontWeight: 400, marginBottom: 6 }}>Connect Outlook</h1>
              <p style={{ fontSize: 14, color: 'var(--color-text-secondary)', marginBottom: 36, lineHeight: 1.6 }}>
                Connect your Microsoft Outlook so Veritas can send daily branch summaries and incident alerts on your behalf.
              </p>
              <div style={{ background: '#E6F1FB', border: '0.5px solid #B5D4F4', borderRadius: 'var(--radius-lg)', padding: '20px 24px', display: 'flex', alignItems: 'center', gap: 16, marginBottom: 20, maxWidth: 480 }}>
                <div style={{ width: 48, height: 48, background: '#0078D4', borderRadius: 'var(--radius-md)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <IconMail size={24} color="white" stroke={1.5} />
                </div>
                <div>
                  <div style={{ fontSize: 14, fontWeight: 400, color: '#185FA5', marginBottom: 3 }}>Microsoft Outlook</div>
                  <div style={{ fontSize: 12, color: '#185FA5', opacity: 0.8 }}>Connect your work Outlook account via Microsoft 365</div>
                </div>
              </div>
              <button style={{ background: '#0078D4', color: '#fff', border: 'none', borderRadius: 'var(--radius-md)', padding: '12px 24px', fontSize: 14, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
                <IconMail size={16} stroke={1.5} /> Connect Microsoft Outlook
              </button>
              <div style={{ fontSize: 12, color: 'var(--color-text-tertiary)', marginTop: 8 }}>You can connect Outlook later from your profile settings.</div>
            </>
          )}

        </div>

        {/* Footer */}
        <div style={{ borderTop: '0.5px solid var(--color-border-light)', padding: '16px 64px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: '#fff', flexShrink: 0 }}>
          <span style={{ fontSize: 12, color: 'var(--color-text-tertiary)' }}>Step {step} of {STEPS.length}</span>
          <div style={{ display: 'flex', gap: 10 }}>
            {step > 1 && (
              <button onClick={handleBack} style={{ background: 'transparent', color: 'var(--color-text-secondary)', border: '0.5px solid var(--color-border-medium)', borderRadius: 'var(--radius-md)', padding: '10px 20px', fontSize: 13, cursor: 'pointer' }}>Back</button>
            )}
            {step < STEPS.length ? (
              <button onClick={handleNext} disabled={step === 4 && hasTank === null} style={{ background: 'var(--color-brand)', color: '#fff', border: 'none', borderRadius: 'var(--radius-md)', padding: '10px 24px', fontSize: 13, cursor: step === 4 && hasTank === null ? 'not-allowed' : 'pointer', opacity: step === 4 && hasTank === null ? 0.5 : 1, display: 'flex', alignItems: 'center', gap: 6 }}>
                Next <IconArrowRight size={15} stroke={1.5} />
              </button>
            ) : (
              <button onClick={handleFinish} style={{ background: 'var(--color-success)', color: '#fff', border: 'none', borderRadius: 'var(--radius-md)', padding: '10px 24px', fontSize: 13, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6 }}>
                <IconCheck size={15} stroke={1.5} /> Go to dashboard
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Asset modal */}
      {showAssetModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100 }}>
          <div style={{ background: '#fff', borderRadius: 'var(--radius-xl)', padding: 28, width: 440, border: '0.5px solid var(--color-border-light)' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
              <div style={{ fontSize: 18, fontWeight: 400 }}>Add asset</div>
              <div onClick={() => setShowAssetModal(false)} style={{ cursor: 'pointer', color: 'var(--color-text-tertiary)' }}><IconX size={20} stroke={1.5} /></div>
            </div>
            <div style={{ marginBottom: 14 }}>
              <label style={{ display: 'block', fontSize: 11, color: 'var(--color-text-secondary)', marginBottom: 6 }}>Asset type</label>
              <div style={{ display: 'flex', gap: 8 }}>
                {ASSET_TYPES.map(t => (
                  <div key={t} onClick={() => setAssetType(t)} style={{ flex: 1, padding: '8px', borderRadius: 'var(--radius-md)', border: '0.5px solid', borderColor: assetType === t ? 'var(--color-brand)' : 'var(--color-border-medium)', background: assetType === t ? 'var(--color-brand-light)' : 'transparent', color: assetType === t ? 'var(--color-brand)' : 'var(--color-text-secondary)', fontSize: 12, cursor: 'pointer', textAlign: 'center', transition: 'all 0.15s' }}>{t}</div>
                ))}
              </div>
            </div>
            {assetType === 'Vehicle' && (
              <div style={{ marginBottom: 14 }}>
                <label style={{ display: 'block', fontSize: 11, color: 'var(--color-text-secondary)', marginBottom: 6 }}>Registration number</label>
                <div style={{ display: 'flex', gap: 8 }}>
                  <input type="text" placeholder="YK23 HBT" value={assetReg} onChange={e => setAssetReg(e.target.value.toUpperCase())} style={{ flex: 1, boxSizing: 'border-box', fontFamily: 'monospace' }} />
                  <button onClick={handleDvlaLookup} disabled={dvlaLoading} style={{ background: 'var(--color-brand)', color: '#fff', border: 'none', borderRadius: 'var(--radius-md)', padding: '0 14px', fontSize: 12, cursor: 'pointer', whiteSpace: 'nowrap' }}>
                    {dvlaLoading ? 'Looking up...' : 'DVLA lookup'}
                  </button>
                </div>
              </div>
            )}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 14 }}>
              <div>
                <label style={{ display: 'block', fontSize: 11, color: 'var(--color-text-secondary)', marginBottom: 6 }}>Make</label>
                <input type="text" placeholder="Ford" value={assetMake} onChange={e => setAssetMake(e.target.value)} style={{ width: '100%', boxSizing: 'border-box' }} />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: 11, color: 'var(--color-text-secondary)', marginBottom: 6 }}>Model</label>
                <input type="text" placeholder="Transit" value={assetModel} onChange={e => setAssetModel(e.target.value)} style={{ width: '100%', boxSizing: 'border-box' }} />
              </div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 20 }}>
              <input type="checkbox" id="pool-check" checked={assetPool} onChange={e => setAssetPool(e.target.checked)} style={{ width: 'auto' }} />
              <label htmlFor="pool-check" style={{ fontSize: 13, color: 'var(--color-text-secondary)', cursor: 'pointer' }}>Pool asset — shared between multiple staff</label>
            </div>
            <div style={{ display: 'flex', gap: 10 }}>
              <button onClick={() => setShowAssetModal(false)} style={{ flex: 1, background: 'var(--color-bg-secondary)', color: 'var(--color-text-secondary)', border: 'none', borderRadius: 'var(--radius-md)', padding: '10px', fontSize: 13, cursor: 'pointer' }}>Cancel</button>
              <button onClick={handleAddAsset} style={{ flex: 2, background: 'var(--color-brand)', color: '#fff', border: 'none', borderRadius: 'var(--radius-md)', padding: '10px', fontSize: 13, cursor: 'pointer' }}>Add asset</button>
            </div>
          </div>
        </div>
      )}

      {/* Staff modal */}
      {showStaffModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100 }}>
          <div style={{ background: '#fff', borderRadius: 'var(--radius-xl)', padding: 28, width: 420, border: '0.5px solid var(--color-border-light)' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
              <div style={{ fontSize: 18, fontWeight: 400 }}>Invite staff</div>
              <div onClick={() => setShowStaffModal(false)} style={{ cursor: 'pointer', color: 'var(--color-text-tertiary)' }}><IconX size={20} stroke={1.5} /></div>
            </div>
            <div style={{ marginBottom: 14 }}>
              <label style={{ display: 'block', fontSize: 11, color: 'var(--color-text-secondary)', marginBottom: 6 }}>Email address</label>
              <input type="email" placeholder="name@company.co.uk" value={staffEmail} onChange={e => setStaffEmail(e.target.value)} style={{ width: '100%', boxSizing: 'border-box' }} />
            </div>
            <div style={{ marginBottom: 20 }}>
              <label style={{ display: 'block', fontSize: 11, color: 'var(--color-text-secondary)', marginBottom: 6 }}>Role</label>
              <div style={{ display: 'flex', gap: 8 }}>
                {['operator', 'driver'].map(r => (
                  <div key={r} onClick={() => setStaffRole(r)} style={{ flex: 1, padding: '10px', borderRadius: 'var(--radius-md)', border: '0.5px solid', borderColor: staffRole === r ? 'var(--color-brand)' : 'var(--color-border-medium)', background: staffRole === r ? 'var(--color-brand-light)' : 'transparent', color: staffRole === r ? 'var(--color-brand)' : 'var(--color-text-secondary)', fontSize: 13, cursor: 'pointer', textAlign: 'center', transition: 'all 0.15s' }}>
                    {r.charAt(0).toUpperCase() + r.slice(1)}
                  </div>
                ))}
              </div>
            </div>
            <div style={{ display: 'flex', gap: 10 }}>
              <button onClick={() => setShowStaffModal(false)} style={{ flex: 1, background: 'var(--color-bg-secondary)', color: 'var(--color-text-secondary)', border: 'none', borderRadius: 'var(--radius-md)', padding: '10px', fontSize: 13, cursor: 'pointer' }}>Cancel</button>
              <button onClick={handleAddStaff} style={{ flex: 2, background: 'var(--color-brand)', color: '#fff', border: 'none', borderRadius: 'var(--radius-md)', padding: '10px', fontSize: 13, cursor: 'pointer' }}>Send invitation</button>
            </div>
          </div>
        </div>
      )}

      {/* Photo crop modal */}
      {showCropModal && (
        <PhotoCropModal
          onSave={handlePhotoSave}
          onClose={() => setShowCropModal(false)}
        />
      )}

    </div>
  );
}